"use client";

import React, { useState } from "react";
import { MapPin, ArrowRight, Utensils } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const districts = [{
  id: "pueblo",
  name: "Pueblo CO",
  description: "Our central hub for rapid dispatch across the city core.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680241/img/pueblo_response_unit.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680241/img/pueblo_response_unit.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680241/img/pueblo_response_unit.png-wvc-sizes",
    alt: "Emergency restoration unit in Pueblo"
  },
  path: "M210,160 Q340,180 360,290 Q250,360 190,280 Z"
}, {
  id: "pueblo-west",
  name: "Pueblo West CO",
  description: "Fast response teams stationed for immediate West side coverage.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680241/img/pueblo_west_flood_cleanup.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680241/img/pueblo_west_flood_cleanup.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680241/img/pueblo_west_flood_cleanup.png-wvc-sizes",
    alt: "Flood cleanup team in Pueblo West"
  },
  path: "M40,140 Q150,180 190,280 Q80,350 30,250 Z"
}, {
  id: "canon-city",
  name: "Canon City CO",
  description: "Dedicated structural drying specialists serving Canon City 24/7.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680241/img/canon_city_restoration.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680241/img/canon_city_restoration.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680241/img/canon_city_restoration.png-wvc-sizes",
    alt: "Restoration services in Canon City"
  },
  path: "M200,40 Q320,20 350,120 Q250,180 180,150 Z"
}, {
  id: "florence",
  name: "Florence CO",
  description: "Rapid emergency response for Florence residents and businesses.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680241/img/florence_emergency_response.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680241/img/florence_emergency_response.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680241/img/florence_emergency_response.png-wvc-sizes",
    alt: "Emergency response in Florence"
  },
  path: "M50,50 Q150,30 180,150 Q80,180 40,120 Z"
}, {
  id: "rye",
  name: "Rye CO",
  description: "Specialized storm damage and flood restoration for the Rye area.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680241/img/rye_storm_damage.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680241/img/rye_storm_damage.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680241/img/rye_storm_damage.png-wvc-sizes",
    alt: "Storm damage restoration in Rye"
  },
  path: "M100,300 Q150,320 200,380 Q100,390 50,350 Z"
}];
function LatestPlacesConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-64 rounded-2xl bg-muted/20 animate-pulse" />)}
      </div>;
  }
  if (error || posts.length === 0) {
    return null;
  }
  return <div className="mt-16 md:mt-24">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h3 className="font-default text-2xl font-bold text-foreground md:text-3xl uppercase tracking-tight">
            Recent Dispatches
          </h3>
          <p className="mt-2 text-muted-foreground font-default">
            Live updates from our emergency restoration teams across Pueblo County.
          </p>
        </div>
        <Button variant="outline" className="hidden md:inline-flex border-secondary/50 text-secondary hover:bg-secondary/10" asChild>
          <Link to="/ServicesPage">View All Services</Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {posts.slice(0, 3).map((post, i) => <Card key={post.id} data-index={i} className="group overflow-hidden border-border/20 bg-card/40 backdrop-blur-xl transition-all hover:shadow-lg hover:shadow-secondary/10">
            <div className="relative h-48 overflow-hidden">
              {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted/30">
                  <Utensils className="h-8 w-8 text-secondary/50" />
                </div>}
              <div className="absolute top-4 left-4">
                <Badge variant="default" className="bg-primary/90 backdrop-blur-md border border-white/10">
                  Active Site
                </Badge>
              </div>
            </div>
            <CardHeader className="p-5 pb-0">
              <CardTitle className="line-clamp-1 text-lg font-default font-bold uppercase">
                {post.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 pt-3">
              <div className="flex items-center text-sm text-muted-foreground font-mono">
                <MapPin className="mr-1.5 h-4 w-4 shrink-0 text-secondary" />
                <span className="line-clamp-1">
                  {post.customFields?.lokasi || "Pueblo, CO"}
                </span>
              </div>
              {post.link && <Button variant="link" className="mt-4 h-auto p-0 text-secondary hover:text-secondary/80" asChild>
                  <Link to={post.link}>
                    Case Details <ArrowRight className="ml-1 h-3 w-3" />
                  </Link>
                </Button>}
            </CardContent>
          </Card>)}
      </div>
      <div className="mt-8 text-center md:hidden">
        <Button variant="outline" className="w-full border-secondary/50 text-secondary" asChild>
          <Link to="/ServicesPage">View All Services</Link>
        </Button>
      </div>
    </div>;
}
export default function JelajahiBerdasarkanWilayah() {
  const [activeDistrict, setActiveDistrict] = useState<string | null>(null);
  const wp_query_params = {
    post_type: "kuliner",
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="260035" id="our-rapid-response-zone" className="dark relative overflow-hidden bg-background text-foreground py-16 md:py-24">
      
      <div className="pointer-events-none absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 opacity-30">
        <div className="h-[500px] w-[500px] rounded-lg bg-secondary/10 blur-3xl" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mb-12 max-w-2xl">
          <Badge variant="secondary" className="mb-4 bg-secondary/20 text-secondary border-secondary/30 font-mono">
            24/7 LIVE DISPATCH
          </Badge>
          <h2 className="font-default text-3xl font-black tracking-tighter text-foreground md:text-4xl lg:text-6xl uppercase">
            Our Rapid Response Zone
          </h2>
          <p className="mt-4 text-lg text-muted-foreground font-default">
            When water damage strikes, every minute counts. We maintain strategically positioned teams across Pueblo County to ensure we arrive faster than anyone else.
          </p>
        </div>

        
        <div className="hidden md:grid md:grid-cols-12 md:gap-12 lg:gap-16 items-center">
          
          <div className="col-span-5 lg:col-span-4 flex flex-col gap-3">
            {districts.map((district, i) => <div key={district.id} data-index={i} className={`group cursor-pointer rounded-2xl border p-5 transition-all duration-300 ${activeDistrict === district.id ? "border-secondary bg-secondary/10 shadow-lg shadow-secondary/5" : "border-border/20 bg-card/20 hover:border-secondary/40 hover:bg-card/40"}`} onMouseEnter={() => setActiveDistrict(district.id)} onMouseLeave={() => setActiveDistrict(null)}>
                <div className="flex items-center justify-between">
                  <h3 className={`font-default text-xl font-bold uppercase transition-colors ${activeDistrict === district.id ? "text-secondary" : "text-foreground group-hover:text-secondary"}`}>
                    {district.name}
                  </h3>
                </div>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2 font-default">
                  {district.description}
                </p>
              </div>)}
            
            <div className="mt-6 p-6 rounded-2xl bg-primary/40 border border-secondary/20 backdrop-blur-md">
              <p className="text-xs font-mono text-secondary uppercase tracking-widest mb-1">Average Response Time</p>
              <p className="font-mono text-4xl font-bold text-foreground">00:28:45</p>
            </div>
          </div>

          
          <div className="col-span-7 lg:col-span-8 relative">
            <div className="aspect-square w-full max-w-[600px] mx-auto relative rounded-3xl bg-card/20 p-8 border border-border/20 backdrop-blur-xl flex items-center justify-center">
              <svg viewBox="0 0 400 400" className="w-full h-full drop-shadow-2xl transition-all duration-500" style={{
              filter: "drop-shadow(0px 10px 30px rgba(0,0,0,0.5))"
            }}>
                {districts.map((district, i) => <path key={district.id} data-index={i} d={district.path} className={`transition-all duration-500 cursor-pointer stroke-[2px] ${activeDistrict === district.id ? "fill-secondary stroke-secondary/40 scale-[1.02] origin-center" : activeDistrict ? "fill-muted/20 stroke-border/20 scale-95 origin-center opacity-30" : "fill-muted/40 stroke-border/40 hover:fill-secondary/30 hover:stroke-secondary/60"}`} onMouseEnter={() => setActiveDistrict(district.id)} onMouseLeave={() => setActiveDistrict(null)} />)}
              </svg>
              
              
              <div className={`absolute bottom-8 left-8 right-8 rounded-xl bg-card/90 backdrop-blur-xl p-5 border border-secondary/30 shadow-2xl transition-all duration-300 transform ${activeDistrict ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0 pointer-events-none"}`}>
                {activeDistrict && <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-mono font-medium text-secondary uppercase tracking-wider">Dispatching To</p>
                      <p className="font-default text-xl font-black text-foreground uppercase">
                        {districts.find(d => d.id === activeDistrict)?.name}
                      </p>
                    </div>
                    <Button size="sm" className="bg-primary text-white hover:bg-primary/90 font-default font-bold uppercase" asChild>
                      <Link to="/ServiceAreasPage">
                        View Area <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        
        <div className="grid grid-cols-1 gap-4 md:hidden">
          {districts.map((district, i) => <Link key={district.id} to="/ServiceAreasPage" className="block group">
              <div data-index={i} className="relative h-40 w-full overflow-hidden rounded-2xl border border-border/20">
                <img src={district.image.src} data-wvc-srcset={district.image.srcset} data-wvc-sizes={district.image.sizes} alt={district.image.alt} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/40 to-transparent" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <div className="flex items-end justify-between">
                    <div>
                      <h3 className="font-default text-xl font-black text-white uppercase">
                        {district.name}
                      </h3>
                      <p className="mt-1 text-sm text-white/80 line-clamp-1 font-default">
                        {district.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Link>)}
        </div>

        
        <PostsProvider wp_query={{
        post_type: "kuliner",
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <LatestPlacesConsumer />
        </PostsProvider>

      </div>
    </section>;
}